import { useState } from 'react';
import { formatCode, generateCode, normalizeCode } from '@/sync/crypto';
import { syncEngine } from '@/sync/engine';
import { HASH_PREFIX } from '@/sync/useSyncLifecycle';
import { useT } from '@/i18n/useT';
import { useSync } from '@/state/syncStore';
import { useToast } from '@/state/toastStore';
import { QrCode } from '@/components/ui/QrCode';

const BUTTON =
  'rounded-xs border border-line bg-surface-2 px-2 py-1.5 text-left hover:border-pip-dim hover:text-pip';

/**
 * Pairing lives beside the backup buttons, because it is the same job done
 * continuously: getting this checklist onto another device.
 */
export function SyncSection() {
  const { t, lang } = useT();
  const code = useSync((s) => s.code);
  const status = useSync((s) => s.status);
  const fault = useSync((s) => s.fault);
  const lastSyncedAt = useSync((s) => s.lastSyncedAt);
  const showToast = useToast((s) => s.show);

  const [entering, setEntering] = useState(false);
  const [draft, setDraft] = useState('');
  const [badCode, setBadCode] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const link = code ? `${window.location.origin}/${HASH_PREFIX}${code}` : '';

  const copy = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      showToast({ message: t('sync.copied') });
    } catch {
      showToast({ message: t('sync.copyFailed') });
    }
  };

  const pair = (next: string): void => {
    syncEngine
      .pair(next)
      .then(() => showToast({ message: t('sync.paired') }))
      .catch(() => showToast({ message: t('sync.pairFailed') }));
  };

  const join = (): void => {
    const normalized = normalizeCode(draft);
    if (!normalized) {
      setBadCode(true);
      return;
    }
    setEntering(false);
    setDraft('');
    setBadCode(false);
    pair(normalized);
  };

  const statusLine = (): string => {
    if (status === 'syncing') return t('sync.statusSyncing');
    if (status === 'error' && fault) return t(`sync.fault.${fault}` as const);
    if (lastSyncedAt === null) return t('sync.statusWaiting');
    return t('sync.statusIdle', {
      time: new Intl.DateTimeFormat(lang, { timeStyle: 'short' }).format(lastSyncedAt),
    });
  };

  return (
    <section className="flex flex-col gap-2 border-t border-line pt-3">
      <h3 className="text-[11px] leading-4 font-semibold tracking-wide text-text-faint uppercase">
        {t('sync.title')}
      </h3>

      {code === null ? (
        <>
          <p className="text-[11px] leading-4 text-text-faint">{t('sync.offNote')}</p>
          {entering ? (
            <>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] leading-4 text-text-faint">
                  {t('sync.enterPrompt')}
                </span>
                <input
                  value={draft}
                  autoFocus
                  spellCheck={false}
                  autoCapitalize="characters"
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setBadCode(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') join();
                  }}
                  className="rounded-xs border border-line bg-surface px-2 py-1 font-display text-[13px] leading-5 tracking-wide text-text focus:border-pip-dim focus:outline-none"
                />
              </label>
              {badCode ? <p className="text-[11px] leading-4 text-danger">{t('sync.badCode')}</p> : null}
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={join} className={BUTTON}>
                  {t('sync.join')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEntering(false);
                    setBadCode(false);
                  }}
                  className={BUTTON}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => pair(generateCode())} className={BUTTON}>
                {t('sync.turnOn')}
              </button>
              <button type="button" onClick={() => setEntering(true)} className={BUTTON}>
                {t('sync.haveCode')}
              </button>
            </div>
          )}
        </>
      ) : (
        <>
          <p
            className={`text-[11px] leading-4 ${status === 'error' ? 'text-amber' : 'text-text-dim'}`}
            role="status"
          >
            {statusLine()}
          </p>

          <p className="text-[11px] leading-4 text-text-faint">{t('sync.codeLabel')}</p>
          <code className="tnum rounded-xs border border-line bg-surface-2 px-2 py-1.5 text-[13px] leading-5 break-all text-pip select-all">
            {formatCode(code)}
          </code>
          <p className="text-[11px] leading-4 text-amber/80">{t('sync.secretNote')}</p>

          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => void copy(link)} className={BUTTON}>
              {t('sync.copyLink')}
            </button>
            <button type="button" onClick={() => setShowQr((v) => !v)} className={BUTTON}>
              {showQr ? t('sync.hideQr') : t('sync.showQr')}
            </button>
          </div>

          {showQr ? (
            <div className="flex flex-col items-center gap-1.5 rounded-xs border border-line bg-surface-2 p-3">
              <QrCode value={link} label={t('sync.qrAlt')} />
              <p className="text-center text-[11px] leading-4 text-text-faint">
                {t('sync.scanNote')}
              </p>
            </div>
          ) : null}

          <p className="text-[11px] leading-4 text-text-faint">{t('sync.limits')}</p>

          <button
            type="button"
            onClick={() => {
              if (window.confirm(t('sync.stopConfirm'))) void syncEngine.stop(false);
            }}
            className={BUTTON}
          >
            {t('sync.stop')}
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(t('sync.eraseConfirm'))) void syncEngine.stop(true);
            }}
            className="rounded-xs border border-danger/40 px-2 py-1.5 text-left text-danger hover:bg-danger/10"
          >
            {t('sync.stopErase')}
          </button>
        </>
      )}
    </section>
  );
}
